/**
 * MyPlaylists — v3.0.134 (Phase 1 completion)
 * Lists all playlists owned by the current user. Create-new is inline
 * (no modal — just a text input + button). Clicking a card takes you
 * to /playlist/:id for the detail view.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ListMusic, Plus, Trash2, Music, Loader2, Lock, Globe, Headphones } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { apiFetch } from '../App';
import MainNav from '../components/MainNav';

export default function MyPlaylists() {
  const nav = useNavigate();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiFetch('/playlists');
      setPlaylists(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(`Load failed: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error('Give your playlist a name');
      return;
    }
    setCreating(true);
    try {
      const { data: pl } = await apiFetch('/playlists', {
        method: 'POST',
        body: JSON.stringify({ name }),
      });
      toast.success(`Created "${pl.name}"`);
      setNewName('');
      nav(`/playlist/${pl.playlist_id}`);
    } catch (err) {
      toast.error(`Create failed: ${err.message || err}`);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(
      `Delete playlist "${name}"?\n\n`
      + `It'll be moved to your Recently deleted bin and kept for 30 days. `
      + `Songs themselves stay in your library.`,
    )) return;
    try {
      await apiFetch(`/playlists/${id}`, { method: 'DELETE' });
      setPlaylists((prev) => prev.filter((p) => p.playlist_id !== id));
      toast.success('Moved to trash — kept for 30 days', {
        action: {
          label: 'View trash',
          onClick: () => nav('/playlists/trash'),
        },
      });
    } catch (err) {
      toast.error(`Delete failed: ${err.message || err}`);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="my-playlists-page">
      <MainNav />
      <div className="max-w-5xl mx-auto px-4 pt-24 pb-16">
        <header className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <ListMusic className="text-emerald-400" size={32} />
              My Playlists
            </h1>
            <p className="text-sm text-zinc-400 mt-1">
              Group songs into playlists. Play them back-to-back, share with a private code, or keep for yourself.
            </p>
          </div>
          <Link
            to="/playlists/trash"
            className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white px-3 py-1.5 rounded border border-white/10 hover:border-white/20 transition-colors"
            data-testid="my-playlists-view-trash-link"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Recently deleted
          </Link>
        </header>

        {/* Create inline */}
        <div className="mb-8 bg-white/5 border border-white/10 rounded-lg p-4">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
            Create a new playlist
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder="e.g. Sarah's Birthday Songs, Chill Study Beats, Anniversary Mix…"
              className="flex-1 min-w-[220px] bg-black/40 border-white/10 text-white"
              data-testid="playlist-name-input"
              maxLength={120}
            />
            <Button
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
              data-testid="playlist-create-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
              Create
            </Button>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-zinc-400 py-12 text-center">Loading playlists…</div>
        ) : playlists.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-white/10 rounded-lg">
            <ListMusic className="mx-auto mb-3 text-zinc-600" size={40} />
            <p className="text-zinc-400">
              No playlists yet — create your first one above.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {playlists.map((p) => (
              <div
                key={p.playlist_id}
                className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg overflow-hidden transition-colors relative group"
                data-testid={`playlist-card-${p.playlist_id}`}
              >
                <Link to={`/playlist/${p.playlist_id}`} className="block">
                  {/* Cover collage / fallback */}
                  <div className="aspect-square bg-gradient-to-br from-emerald-500/20 to-purple-500/20 relative">
                    {p.cover_url ? (
                      <img
                        src={p.cover_url}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                        data-testid={`playlist-card-cover-${p.playlist_id}`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ListMusic size={48} className="text-white/40" />
                      </div>
                    )}
                    <span
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 backdrop-blur"
                      title={p.is_public ? 'Public with access code' : 'Private'}
                    >
                      {p.is_public ? (
                        <Globe className="w-3.5 h-3.5 text-emerald-300" />
                      ) : (
                        <Lock className="w-3.5 h-3.5 text-zinc-300" />
                      )}
                    </span>
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-white pr-8 line-clamp-2">{p.name}</h3>
                    {p.description && (
                      <p className="text-xs text-zinc-400 line-clamp-2 mt-1">{p.description}</p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-zinc-500 mt-2">
                      <span className="flex items-center gap-1">
                        <Music className="w-3.5 h-3.5" />
                        {p.song_count} song{p.song_count === 1 ? '' : 's'}
                      </span>
                      {p.is_public && (
                        <span
                          className="flex items-center gap-1 text-emerald-300"
                          title="Times this share link was opened"
                          data-testid={`playlist-plays-${p.playlist_id}`}
                        >
                          <Headphones className="w-3.5 h-3.5" />
                          {p.play_count ?? 0} play{(p.play_count ?? 0) === 1 ? '' : 's'}
                        </span>
                      )}
                      {p.is_public && (
                        <span className="font-mono bg-emerald-500/10 text-emerald-300 px-1.5 py-0.5 rounded">
                          Code {p.access_code}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
                <button
                  onClick={(e) => { e.preventDefault(); handleDelete(p.playlist_id, p.name); }}
                  className="absolute bottom-2 right-2 p-1.5 rounded opacity-0 group-hover:opacity-100 hover:bg-rose-500/20 text-zinc-400 hover:text-rose-300 transition-opacity bg-black/60"
                  data-testid={`playlist-delete-${p.playlist_id}`}
                  title="Delete playlist"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
